import { Payer } from "@redotech/congo-enums/payer";
import type { UncoveredPayer } from "@redotech/redo-return-core-common/return-flow";
import { isForcedManualLabelMode } from "@redotech/redo-return-core-common/return-flow/return-option";
import { ManualLabelMode } from "@redotech/redo-return-core-common/return-flow/return-option-enums";
import { RadioGroupOption } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";

/** Where a manual-label mode change leaves an option's label payer. */
export interface ManualLabelLabelPayerTransition {
  labelPayer: UncoveredPayer;
  /** Handed back when the option stops forcing the manual label. */
  rememberedLabelPayer: UncoveredPayer | undefined;
}

/**
 * Only forcing the manual label rules out a label of ours, so only it leaves
 * the shopper paying. The payer the merchant had configured — Safety net
 * included — is remembered on the way in and handed back when they switch to
 * shopper choice or turn the manual label off, because either one may still
 * take our label. An option the editor loaded already forcing has nothing
 * remembered, so its payer stays where the merchant left it.
 */
export function manualLabelModeLabelPayer({
  currentMode,
  nextMode,
  labelPayer,
  rememberedLabelPayer,
}: {
  currentMode: ManualLabelMode;
  nextMode: ManualLabelMode;
  labelPayer: UncoveredPayer;
  rememberedLabelPayer: UncoveredPayer | undefined;
}): ManualLabelLabelPayerTransition {
  if (isForcedManualLabelMode(nextMode)) {
    return isForcedManualLabelMode(currentMode)
      ? { labelPayer, rememberedLabelPayer }
      : { labelPayer: Payer.Customer, rememberedLabelPayer: labelPayer };
  }
  if (
    isForcedManualLabelMode(currentMode) &&
    rememberedLabelPayer !== undefined
  ) {
    return {
      labelPayer: rememberedLabelPayer,
      rememberedLabelPayer: undefined,
    };
  }
  return { labelPayer, rememberedLabelPayer };
}

export function manualLabelOptions(
  t: (key: string, options?: Record<string, string>) => string,
): RadioGroupOption<ManualLabelMode>[] {
  const brand = { brandName: "Redo" };
  return [
    {
      id: ManualLabelMode.MERCHANT_FORCE,
      label: t("Merchant provides the label"),
      description: t(
        "The merchant handles the label; the customer cannot get a {{brandName}} label.",
        brand,
      ),
    },
    {
      id: ManualLabelMode.SHOPPER_FORCE,
      label: t("Shopper buys their own label"),
      description: t(
        "The customer buys their own postage; they cannot get a {{brandName}} label.",
        brand,
      ),
    },
    {
      id: ManualLabelMode.SHOPPER_CHOICE,
      label: t("Let the shopper choose"),
      description: t(
        "The customer picks between a {{brandName}} label and buying their own.",
        brand,
      ),
    },
  ];
}
